import { OpenSheetMusicDisplay } from '@museflow-ai/opensheetmusicdisplay';

// The phrase's box, in staff spaces (the gap between two staff lines): room above the
// treble staff for fingerings and high notes, the two staves and the gap between them,
// and room below the bass staff. The box is this many staff spaces tall at every width.
const SPACES_ABOVE_TREBLE = 2.8;
const SPACES_BETWEEN_STAVES = 8;
const SPACES_BELOW_BASS = 3.6;
export const PREVIEW_PHRASE_HEIGHT_SPACES =
  SPACES_ABOVE_TREBLE + 4 + SPACES_BETWEEN_STAVES + 4 + SPACES_BELOW_BASS;

// OSMD draws one staff space as 10 units at zoom 1.
const OSMD_UNITS_PER_STAFF_SPACE = 10;

export interface RenderedPreviewPhrase {
  // The SVG markup, drawn at the requested width.
  svg: string;
  // How far to move the SVG down so the treble staff sits at the same place in every box.
  offsetTop: number;
}

export const previewStaffSpace = (boxHeight: number): number =>
  boxHeight / PREVIEW_PHRASE_HEIGHT_SPACES;

// Least room between a fingering and a stem, in staff spaces.
const FINGERING_STEM_CLEARANCE_SPACES = 0.3;

interface StemLine {
  x: number;
  top: number;
  bottom: number;
  halfWidth: number;
}

// A stem is drawn as one vertical line: "M x y1 L x y2".
const stemLineOf = (stem: SVGPathElement): StemLine | null => {
  const match = /M\s*(-?[\d.]+)[\s,]+(-?[\d.]+)\s*L\s*(-?[\d.]+)[\s,]+(-?[\d.]+)/.exec(
    stem.getAttribute('d') ?? '',
  );
  if (!match) return null;
  const [x1, y1, , y2] = match.slice(1).map(Number);
  const strokeWidth = Number(stem.getAttribute('stroke-width')) || 1;
  return { x: x1, top: Math.min(y1, y2), bottom: Math.max(y1, y2), halfWidth: strokeWidth / 2 };
};

// A fingering is centred on its note, outside the staff. Where the note's stem reaches out
// past the staff too (up from a high treble note, down from a high bass note), the stem
// runs right beside the digit. Such a digit moves sideways, away from the stem, until it is
// clear of it. Done on the drawn SVG, in its own units (the zoom scales them to pixels).
const keepFingeringsClearOfStems = (svg: SVGSVGElement, staffSpace: number): void => {
  const viewBoxWidth = Number((svg.getAttribute('viewBox') ?? '').trim().split(/[\s,]+/)[2]);
  const drawnWidth = Number.parseFloat(svg.getAttribute('width') ?? '');
  const pixelsPerUnit = viewBoxWidth > 0 && drawnWidth > 0 ? drawnWidth / viewBoxWidth : 1;
  const clearance = (FINGERING_STEM_CLEARANCE_SPACES * staffSpace) / pixelsPerUnit;
  const stems = Array.from(svg.querySelectorAll<SVGPathElement>('path.vf-stem'))
    .map(stemLineOf)
    .filter((stem): stem is StemLine => stem !== null);
  const fingerings = Array.from(svg.querySelectorAll('text')).filter((text) =>
    /^[1-5]$/.test(text.textContent?.trim() ?? ''),
  );
  fingerings.forEach((fingering) => {
    const box = fingering.getBBox();
    const stemBeside = stems.find(
      (stem) =>
        stem.top < box.y + box.height &&
        stem.bottom > box.y &&
        stem.x + stem.halfWidth + clearance > box.x &&
        stem.x - stem.halfWidth - clearance < box.x + box.width,
    );
    if (!stemBeside) return;
    const stemOnTheLeft = stemBeside.x <= box.x + box.width / 2;
    const shift = stemOnTheLeft
      ? stemBeside.x + stemBeside.halfWidth + clearance - box.x
      : stemBeside.x - stemBeside.halfWidth - clearance - (box.x + box.width);
    fingering.setAttribute('x', String(Number(fingering.getAttribute('x') ?? 0) + shift));
  });
};

// Engraves the phrase as SVG at a fixed staff space, its one system justified to the width:
// a wider box spreads the notes out, a narrower one brings them closer, the symbols keep
// their size. Done in a detached, hidden element that is removed afterwards.
export const renderPreviewPhraseSvg = async (
  musicXml: string,
  barCount: number,
  width: number,
  staffSpace: number,
): Promise<RenderedPreviewPhrase> => {
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  Object.assign(host.style, {
    position: 'absolute',
    left: '-100000px',
    top: '0',
    width: `${width}px`,
    visibility: 'hidden',
    pointerEvents: 'none',
  });
  document.body.appendChild(host);
  try {
    const osmd = new OpenSheetMusicDisplay(host, {
      backend: 'svg',
      autoResize: false,
      pageFormat: 'Endless',
      drawTitle: false,
      drawSubtitle: false,
      drawComposer: false,
      drawLyricist: false,
      drawCredits: false,
      drawPartNames: false,
      drawPartAbbreviations: false,
      drawMeasureNumbers: false,
      drawMetronomeMarks: false,
      drawFingerings: true,
      fingeringInsideStafflines: false,
      stretchLastSystemLine: true,
      autoBeam: false,
      disableCursor: true,
      followCursor: false,
    });
    const rules = osmd.EngravingRules;
    // The lighter of the two note fonts, as the sight-reading screens use.
    rules.DefaultVexFlowNoteFont = 'gonville';
    // Room on the left for the brace that joins the two staves.
    rules.PageLeftMargin = 2;
    rules.PageRightMargin = 0.4;
    rules.PageTopMargin = 0;
    rules.PageBottomMargin = 0;
    rules.SystemLeftMargin = 0;
    rules.SystemRightMargin = 0;
    rules.BetweenStaffDistance = SPACES_BETWEEN_STAVES;
    rules.StaffDistance = SPACES_BETWEEN_STAVES;
    rules.MinimumDistanceBetweenSystems = 0;
    // The engraver closes the gap between the staves as far as the notes allow; held
    // at this minimum, the two staves sit the same distance apart in every phrase.
    rules.MinimumStaffLineDistance = SPACES_BETWEEN_STAVES;
    rules.FingeringPositionFromXML = true;
    rules.RenderXMeasuresPerLineAkaSystem = barCount;
    rules.VoiceSpacingMultiplierVexflow = 0.6;
    rules.VoiceSpacingAddendVexflow = 2;

    await osmd.load(musicXml);
    // Loading resets the zoom, so it is set afterwards.
    osmd.Zoom = staffSpace / OSMD_UNITS_PER_STAFF_SPACE;
    osmd.render();

    const systems = osmd.GraphicSheet.MusicPages[0]?.MusicSystems ?? [];
    if (systems.length !== 1) {
      throw new Error(`The phrase took ${systems.length} lines at ${width}px`);
    }
    const svg = host.querySelector('svg');
    if (!svg) throw new Error('The phrase drew no SVG');
    const trebleTop =
      systems[0].StaffLines[0].PositionAndShape.AbsolutePosition.y *
      OSMD_UNITS_PER_STAFF_SPACE *
      osmd.Zoom;
    keepFingeringsClearOfStems(svg, staffSpace);
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    return { svg: svg.outerHTML, offsetTop: SPACES_ABOVE_TREBLE * staffSpace - trebleTop };
  } finally {
    host.remove();
  }
};
